<template>
  <v-theme-provider theme="dark">
    <div class="clothing-timeline-refactor">
      <TimelineTopBar 
        :is-playing="isPlaying"
        :loading="loading"
        :pool-groups="sharedPoolGroups"
        :selected-segments="selectedSegments"
        :active-tasks="exportTasks"
        :transcription-id="transcriptionId"
        @export-click="onExport" 
        @switch-to-pool="switchToClipPool"
        @history-click="onHistory" 
        @toggle-play="togglePlay"
        @clear-selection="clearSharedPool"
        @download-file="downloadFile"
        @open-progress-dialog="showProgressDialog = true"
        @clip-jump="jumpToSec"
        @remove-clip="removeSegment"
        @reorder-group-clips="reorderSharedGroup"
      />

      <!-- 导出选项对话框 -->
      <v-dialog v-model="showExportDialog" max-width="500">
        <v-card color="#1e1e1e" theme="dark">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-export</v-icon>
            提交导出任务
          </v-card-title>
          <v-card-text>
            <div class="mb-4">
              已选 <strong>{{ selectedSegments.length }}</strong> 段片段，共计 <strong>{{ totalDuration.toFixed(1) }}</strong> 秒。
            </div>
            
            <v-switch
              v-model="exportMerge"
              label="合并导出一个视频"
              color="primary"
              density="compact"
              hide-details
              class="mb-2"
            ></v-switch>
            <div class="text-caption text-grey mb-4">
              开启后，选中的所有片段将按顺序合并为一个导出文件。
            </div>

            <v-text-field
              v-model="exportRemark"
              label="任务备注（可选）"
              placeholder="例如：换装对比导出"
              variant="outlined"
              density="compact"
              hide-details
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showExportDialog = false">取消</v-btn>
            <v-btn color="primary" variant="tonal" @click="confirmExport" :loading="loading">确认提交</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- 导出进度对话框 -->
      <v-dialog v-model="showProgressDialog" max-width="600" persistent>
        <v-card color="#1e1e1e" theme="dark">
          <v-card-title class="d-flex align-center">
            <v-icon color="warning" class="mr-2">mdi-progress-clock</v-icon>
            导出进度
            <v-spacer></v-spacer>
            <v-btn icon="mdi-close" variant="text" size="small" @click="showProgressDialog = false"></v-btn>
          </v-card-title>
          <v-card-text style="max-height: 400px; overflow-y: auto;">
            <v-list bg-color="transparent">
              <v-list-item v-for="(task, index) in exportTasks" :key="task.id || index" class="mb-2 border rounded border-grey-darken-3">
                <template v-slot:prepend>
                  <v-progress-circular v-if="task.status === 'processing' || task.status === 'pending'" indeterminate size="24" color="primary" class="mr-3"></v-progress-circular>
                  <v-icon v-else-if="task.status === 'completed'" color="success" class="mr-3">mdi-check-circle</v-icon>
                  <v-icon v-else-if="task.status === 'failed'" color="error" class="mr-3">mdi-alert-circle</v-icon>
                </template>
                <v-list-item-title>
                  {{ task.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  状态: 
                  <span v-if="task.status === 'completed'" class="text-success">{{ task.message || '完成' }}</span>
                  <span v-else-if="task.status === 'failed'" class="text-error">{{ task.message || '失败' }}</span>
                  <span v-else class="text-warning">{{ task.message || '排队中...' }}</span>
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                    v-if="task.file_url"
                    color="success"
                    size="small"
                    variant="tonal"
                    @click="downloadFile(task.file_url, task.name)"
                  >
                    下载
                  </v-btn>
                </template>
              </v-list-item>
              
              <div v-if="exportTasks.length === 0" class="text-center py-4 text-grey">
                当前没有正在导出的任务。可以在右上角通过“切片历史”查看往期记录。
              </div>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <div class="text-caption text-grey ml-2">关闭此窗口不会影响后台任务继续执行</div>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showProgressDialog = false">后台处理</v-btn>
            <v-btn v-if="exportTasks.every(t => t.status === 'completed' || t.status === 'failed')" color="primary" variant="tonal" @click="clearFinishedTasks">确认</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- 历史记录对话框 -->
      <v-dialog v-model="showHistoryDialog" max-width="800">
        <v-card color="#1e1e1e" theme="dark">
          <v-card-title class="d-flex align-center">
            <v-icon color="#00d2d3" class="mr-2">mdi-history</v-icon>
            导出历史
            <v-spacer></v-spacer>
            <v-btn icon="mdi-refresh" variant="text" size="small" @click="fetchHistory" :loading="historyLoading"></v-btn>
          </v-card-title>
          <v-card-text style="max-height: 500px; overflow-y: auto;">
            <v-list bg-color="transparent">
              <v-list-item v-for="(item, index) in historySlices" :key="index" class="mb-2 border rounded">
                <template v-slot:prepend>
                  <v-icon :color="getStatusColor(item)">
                    {{ getStatusIcon(item) }}
                  </v-icon>
                </template>
                <v-list-item-title>
                  {{ item.remark || '未命名任务' }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.commit_ts || '未知时间' }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="mt-1 text-wrap text-caption text-grey">
                  {{ item.text ? (item.text.length > 50 ? item.text.substring(0, 50) + '...' : item.text) : '无内容' }}
                </v-list-item-subtitle>
                <template v-slot:append>
                  <div class="d-flex align-center">
                    <span class="mr-4 text-caption" :style="{ color: getStatusColor(item) }">
                      {{ getStatusText(item) }}
                    </span>
                    <v-btn
                      v-if="item.finish_ts && item.file_url"
                      color="success"
                      size="small"
                      variant="tonal"
                      @click="downloadFile(item.file_url, item.slice_key_path)"
                    >
                      下载
                    </v-btn>
                    <v-progress-circular
                      v-else-if="!item.finish_ts"
                      indeterminate
                      size="20"
                      width="2"
                    ></v-progress-circular>
                  </div>
                </template>
              </v-list-item>
              <div v-if="historySlices.length === 0 && !historyLoading" class="text-center py-8 text-grey">
                暂无导出记录
              </div>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showHistoryDialog = false">关闭</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div class="main-layout-content">
        <splitpanes horizontal class="default-theme">
          <pane size="65">
            <splitpanes :key="hasPoseData ? 'with-pose-inspector' : 'without-pose-inspector'" class="default-theme">
              <pane :size="hasPoseData ? 30 : 30" :min-size="hasPoseData ? 20 : 20">
                <TimelineSidebar 
                  v-model:transcriptionId="transcriptionId"
                  :loading="loading"
                  :status="timelineData?.status"
                  @generate="generateData"
                  @fetch="fetchData"
                />
              </pane>
              <pane :size="hasPoseData ? 50 : 70">
                <TimelinePlayer 
                  ref="playerRef"
                  :video-url="videoUrl"
                  :target-visible="targetPreviewVisible"
                  :is-playing="isPlaying"
                  @timeupdate="onTimeUpdate"
                  @duration="onDurationChange"
                  @seeked="onVideoSeeked"
                  @hidden-seeked="onHiddenVideoSeeked"
                  @clear-target="clearTarget"
                  @play="isPlaying = true"
                  @pause="isPlaying = false"
                  @toggle-play="togglePlay"
                  @download-image="onDownloadImage"
                  @add-to-pool="addClipToPool"
                />
              </pane>
              <pane v-if="hasPoseData" size="20" min-size="15">
                <TimelineInspector 
                  v-model:filterProfile="filterProfile"
                  v-model:scaleSensitive="scaleSensitive"
                  v-model:posSensitive="posSensitive"
                  v-model:filterSameColor="filterSameColor"
                  v-model:colorThreshold="colorThreshold"
                  :selected-node="selectedNode"
                  :searching="searching"
                  :stats-text="statsText"
                  :profile-options="profileOptions"
                  :results="searchResults"
                  @search="searchPoses"
                  @download-image="onDownloadImage"
                  @clear-results="clearResults"
                  @jump="jumpToSec"
                />
              </pane>
            </splitpanes>
          </pane>
          <pane size="35">
            <div class="timeline-container-wrapper" style="height: 100%; display: flex; flex-direction: column; background-color: #1e1e1e;">
              <!-- 顶部工具条：绝对时间跳转 -->
              <div class="timeline-toolbar">
                <div class="toolbar-left">
                  <div class="toolbar-title">
                    <v-icon size="small" class="mr-1">mdi-chart-timeline-variant</v-icon>
                    主视频时间轴
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="x-small" color="grey-lighten-1" class="ml-1 cursor-pointer">mdi-information</v-icon>
                      </template>
                      <div class="text-caption">
                        <strong>操作说明：</strong><br>
                        • <strong>选择范围：</strong>Shift + 拖动鼠标<br>
                        • <strong>微调边缘：</strong>拖动片段左右两侧出现的白色手柄<br>
                        • <strong>快捷键：</strong><br>
                        &nbsp;&nbsp; - <kbd>I</kbd>: 标记入点<br>
                        &nbsp;&nbsp; - <kbd>O</kbd>: 标出点<br>
                        &nbsp;&nbsp; - <kbd>Enter</kbd>: 确认选区<br>
                        &nbsp;&nbsp; - <kbd>Esc</kbd>: 取消当前未确认的选区
                      </div>
                    </v-tooltip>
                  </div>

                  <v-menu :close-on-content-click="false" location="bottom start">
                    <template v-slot:activator="{ props: menuProps }">
                      <v-btn
                        v-bind="menuProps"
                        size="x-small"
                        variant="tonal"
                        class="track-layer-filter-btn"
                        :color="visibleTrackGroups.length < trackGroupOptions.length ? 'primary' : undefined"
                      >
                        <v-icon start size="small">mdi-layers-triple-outline</v-icon>
                        图层 {{ visibleTrackGroups.length }}/{{ trackGroupOptions.length }}
                      </v-btn>
                    </template>
                    <v-card class="track-layer-filter-menu" theme="dark" @click.stop @mousedown.stop>
                      <div class="track-layer-filter-header">
                        <span>显示轨道分组</span>
                        <div>
                          <v-btn size="x-small" variant="text" color="primary" @click="showAllTrackGroups">全选</v-btn>
                          <v-btn size="x-small" variant="text" color="grey" @click="visibleTrackGroups = []">清空</v-btn>
                        </div>
                      </div>
                      <v-divider></v-divider>
                      <label v-for="group in trackGroupOptions" :key="group.value" class="track-layer-filter-option">
                        <v-checkbox-btn
                          :model-value="visibleTrackGroups.includes(group.value)"
                          :color="group.color"
                          density="compact"
                          @update:model-value="toggleTrackGroup(group.value, $event)"
                        ></v-checkbox-btn>
                        <v-icon :color="group.color" size="small">{{ group.icon }}</v-icon>
                        <span>{{ group.title }}</span>
                      </label>
                    </v-card>
                  </v-menu>
                </div>

                <div class="toolbar-right">
                  <div class="nav-segment-controls mr-2">
                    <v-btn 
                      variant="tonal" 
                      size="x-small" 
                      class="nav-segment-btn"
                      title="上一段 (同直播间)"
                      @click="handleNavigate('prev')"
                      :disabled="loading"
                    >
                      <v-icon size="x-small" class="mr-1">mdi-arrow-left</v-icon>
                      上一段
                    </v-btn>
                    <v-btn 
                      variant="tonal" 
                      size="x-small" 
                      class="nav-segment-btn ml-1"
                      title="下一段 (同直播间)"
                      @click="handleNavigate('next')"
                      :disabled="loading"
                    >
                      下一段
                      <v-icon size="x-small" class="ml-1">mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>

                  <div class="toolbar-jump-box">
                    <span class="jump-label">时间跳转:</span>
                    <div v-if="!isEditingTime" class="time-display-clickable" @click="startEditingTime" title="点击编辑绝对时间进行跳转">
                      {{ currentDisplayTime || '--:--:--' }}
                    </div>
                    <input 
                      v-else
                      ref="timeInputRef"
                      class="time-jump-input"
                      v-model="editableTimeStr"
                      @blur="handleTimeSeek"
                      @keyup.enter="handleTimeSeek"
                      @keyup.esc="isEditingTime = false"
                      placeholder="HH:MM:SS 或 YYYY-MM-DD HH:MM:SS"
                    />
                    <v-btn 
                      icon="mdi-crosshairs-gps" 
                      variant="text" 
                      size="x-small" 
                      class="locate-btn"
                      title="滚动定位到当前播放位置"
                      @click="handleLocate"
                    ></v-btn>
                  </div>
                </div>
              </div>

              <TimelineTrackArea 
                ref="trackAreaRef"
                v-model:px-per-sec="pxPerSec"
                :timeline-data="timelineData"
                :max-seconds="maxSeconds"
                :playhead-pos="playheadPos"
                :highlights="highlights"
                :selected-segments="selectedSegments"
                :visible-track-groups="visibleTrackGroups"
                @click-sec="onTimelineClickSec"
                @add-segment="addSegment"
                @remove-segment="removeSegment"
                @update-segment="updateSegment"
              />
            </div>
          </pane>
        </splitpanes>
      </div>
    </div>
  </v-theme-provider>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import { Splitpanes, Pane } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'

import TimelineTopBar from './parts/TimelineTopBar.vue'
import TimelineSidebar from './parts/TimelineSidebar.vue'
import TimelinePlayer from './parts/TimelinePlayer.vue'
import TimelineInspector from './parts/TimelineInspector.vue'
import TimelineTrackArea from './parts/TimelineTrackArea.vue'

import { BASE_URL } from '@/config'
import MessageUtil from '@/utils/MessageUtil'
import { 
  get_recent_user_slices, 
  create_batch_slices,
  generate_clothing_timeline,
  get_clothing_timeline,
  get_adjacent_transcription,
  search_clothing_poses,
  download_frame_image,
  download_clip_video,
  get_video_material_info
} from '@/services/api.js'

// References
const router = useRouter()
const store = useStore()
const playerRef = ref(null)
const trackAreaRef = ref(null)
const timeInputRef = ref(null)

// State
const transcriptionId = ref('')
const loading = ref(false)
const timelineData = ref(null)
const videoUrl = ref('')
const currentVideoName = ref('')
const pxPerSec = ref(1)
const maxSeconds = ref(60)

const selectedNode = ref(null)
const selectedProfile = ref(null)
const selectedSec = ref(null)
const playheadPos = ref(0)
const highlights = ref({})
const isLocked = ref(false)
const isPlaying = ref(false)

const trackGroupOptions = [
  { title: '商品分组', value: 'commodity', icon: 'mdi-shopping', color: 'orange' },
  { title: '说话人分组', value: 'speaker', icon: 'mdi-account-voice', color: 'light-green-accent-2' },
  { title: '画面分组', value: 'visual', icon: 'mdi-image-outline', color: 'cyan-accent-3' },
  { title: '声音分组', value: 'audio', icon: 'mdi-volume-high', color: 'cyan' }
]
const visibleTrackGroups = ref(trackGroupOptions.map(group => group.value))

const showAllTrackGroups = () => {
  visibleTrackGroups.value = trackGroupOptions.map(group => group.value)
}

const toggleTrackGroup = (group, visible) => {
  if (visible) {
    if (!visibleTrackGroups.value.includes(group)) {
      visibleTrackGroups.value = [...visibleTrackGroups.value, group]
    }
    return
  }
  visibleTrackGroups.value = visibleTrackGroups.value.filter(item => item !== group)
}

// 姿态搜索依赖人物轨迹节点中的姿态向量；无姿态数据时释放右侧空间给播放器。
const hasPoseData = computed(() => Object.values(timelineData.value?.tracks || {}).some(history =>
  Array.isArray(history) && history.some(node => Array.isArray(node?.norm_pose) && node.norm_pose.length > 0)
))

// Time Jumping State
const isEditingTime = ref(false)
const editableTimeStr = ref('')

// Shared clip pool: Vuex is the single source of truth for both timeline and multi-clipper pages.
const toTimelineSegment = (segment, groupIndex) => {
  const words = segment.words || []
  const firstWordStart = words.length > 0 ? Number(words[0].start_time) : 0
  const lastWordEnd = words.length > 0 ? Number(words[words.length - 1].end_time) : firstWordStart
  const rawStart = Number(segment.start_time)
  const rawEnd = Number(segment.end_time)
  const startTime = Number.isFinite(rawStart) ? rawStart : firstWordStart
  const endTime = Number.isFinite(rawEnd) ? rawEnd : lastWordEnd
  return {
    ...segment,
    start: startTime / 1000,
    end: endTime / 1000,
    _poolGroupIndex: groupIndex
  }
}

const sharedPoolGroups = computed(() => (store.state.multiClipper.groups || []).map((group, groupIndex) => ({
  ...group,
  groupIndex,
  isCurrent: groupIndex === store.state.multiClipper.currentGroupIndex,
  segments: (group.segments || []).map(segment => toTimelineSegment(segment, groupIndex))
})))

const selectedSegments = computed(() => sharedPoolGroups.value.flatMap(group => group.segments))
const showExportDialog = ref(false)
const exportMerge = ref(false)
const exportRemark = ref('')
const showHistoryDialog = ref(false)
const historyLoading = ref(false)
const historySlices = ref([])

// Progress Tracking
const showProgressDialog = ref(false)
const exportTasks = ref([])
let pollingTimer = null

const totalDuration = computed(() => {
  return selectedSegments.value.reduce((acc, seg) => acc + Math.max(0, seg.end - seg.start), 0)
})

const currentDisplayTime = computed(() => {
  if (!timelineData.value?.shooting_time) return ''
  const shootTime = new Date(timelineData.value.shooting_time).getTime()
  if (isNaN(shootTime)) return timelineData.value.shooting_time
  
  const currentMs = shootTime + (playheadPos.value * 1000)
  const date = new Date(currentMs)
  
  const pad = n => n < 10 ? '0' + n : n
  const Y = date.getFullYear()
  const M = pad(date.getMonth() + 1)
  const D = pad(date.getDate())
  const h = pad(date.getHours())
  const m = pad(date.getMinutes())
  const s = pad(date.getSeconds())
  return `${Y}-${M}-${D} ${h}:${m}:${s}`
})

// Sync editable time with current display time when not editing
watch(currentDisplayTime, (newVal) => {
    if (!isEditingTime.value) {
        editableTimeStr.value = newVal
    }
})

const startEditingTime = () => {
    if (!timelineData.value?.shooting_time) return
    isEditingTime.value = true
    nextTick(() => {
        if (timeInputRef.value) {
            timeInputRef.value.focus()
        }
    })
}

const handleTimeSeek = () => {
    if (!isEditingTime.value) return
    
    if (!timelineData.value?.shooting_time || !editableTimeStr.value) {
        isEditingTime.value = false
        editableTimeStr.value = currentDisplayTime.value
        return
    }
    
    const shootTimeMs = new Date(timelineData.value.shooting_time).getTime()
    if (isNaN(shootTimeMs)) {
        isEditingTime.value = false
        return
    }
    
    const durationSec = timelineData.value.duration || maxSeconds.value || 0
    
    const formatYMD = (date) => {
        const pad = n => n < 10 ? '0' + n : n
        const Y = date.getFullYear()
        const M = pad(date.getMonth() + 1)
        const D = pad(date.getDate())
        return `${Y}-${M}-${D}`
    }

    const parseUserInputTime = (inputStr, baseTimeMs, durSec) => {
        if (!inputStr) return NaN;
        inputStr = inputStr.trim();
        
        let targetTime = NaN;

        // 1. Try parsing as full absolute time
        targetTime = new Date(inputStr).getTime();
        if (!isNaN(targetTime) && inputStr.includes('-') && inputStr.includes(':')) {
            return targetTime;
        }

        // 2. Check if it's a relative time format (MM:SS or HH:MM:SS)
        const timeFormatRegex = /^(\d+):(\d+)(?::(\d+))?$/;
        const match = inputStr.match(timeFormatRegex);
        if (match) {
            let hours = 0, minutes = 0, seconds = 0;
            if (match[3] !== undefined) {
                hours = parseInt(match[1], 10);
                minutes = parseInt(match[2], 10);
                seconds = parseInt(match[3], 10);
            } else {
                minutes = parseInt(match[1], 10);
                seconds = parseInt(match[2], 10);
            }
            const offsetSec = hours * 3600 + minutes * 60 + seconds;
            
            // If the input has hours and the calculated offset is way larger than duration,
            // it might be a time-of-day on the shooting date.
            if (match[3] !== undefined && durSec && offsetSec > durSec) {
                const shootDateStr = formatYMD(new Date(baseTimeMs));
                const absoluteAttempt = new Date(`${shootDateStr} ${inputStr}`).getTime();
                if (!isNaN(absoluteAttempt)) {
                    return absoluteAttempt;
                }
            }
            
            return baseTimeMs + offsetSec * 1000;
        }
        
        // 3. Check if it's pure seconds
        if (/^\d+(\.\d+)?$/.test(inputStr)) {
            return baseTimeMs + parseFloat(inputStr) * 1000;
        }

        // 4. Try parsing as time-of-day on the shooting date
        const shootDateStr = formatYMD(new Date(baseTimeMs));
        targetTime = new Date(`${shootDateStr} ${inputStr}`).getTime();
        if (!isNaN(targetTime)) {
            return targetTime;
        }

        return NaN;
    }
    
    const targetMs = parseUserInputTime(editableTimeStr.value, shootTimeMs, durationSec)
    
    if (isNaN(targetMs)) {
        MessageUtil.warning('无效的时间格式')
        isEditingTime.value = false
        editableTimeStr.value = currentDisplayTime.value
        return
    }
    
    const diffMs = targetMs - shootTimeMs
    const seekTime = Math.max(0, diffMs / 1000)
    
    if (playerRef.value && playerRef.value.videoPlayer) {
        playerRef.value.videoPlayer.currentTime = seekTime
    }
    
    isEditingTime.value = false
}

const handleNavigate = async (direction) => {
  const tid = transcriptionId.value.trim() || timelineData.value?.transcription_id
  if (!tid) {
    MessageUtil.warning("无有效的 Transcription ID")
    return
  }
  
  loading.value = true
  try {
    const res = await get_adjacent_transcription({
      transcription_id: tid,
      direction: direction,
      same_source: true
    })
    
    let data = res
    if (res && res.data) {
        data = res.data.data ? res.data.data : res.data
    }
    
    if (data && data.transcription_id) {
        transcriptionId.value = data.transcription_id
        await nextTick()
        fetchData()
    } else {
        MessageUtil.info(direction === 'next' ? "已经是最后一段了" : "已经是第一段了")
    }
  } catch (err) {
    console.error('Failed to navigate segment:', err)
    MessageUtil.error('切换片段失败')
  } finally {
    loading.value = false
  }
}

const handleLocate = () => {
    if (trackAreaRef.value && trackAreaRef.value.viewport) {
        const viewport = trackAreaRef.value.viewport
        const sidebarWidth = 100 // Matches TimelineTrackArea.vue constant
        const x = playheadPos.value * pxPerSec.value + sidebarWidth
        const width = viewport.clientWidth
        
        // Center the playhead in the viewport
        viewport.scrollLeft = Math.max(0, x - width / 2)
    }
}

const filterProfile = ref('all')
const scaleSensitive = ref(false)
const posSensitive = ref(false)
const filterSameColor = ref(false)
const colorThreshold = ref(0.8)
const profileOptions = ref([{ title: '所有人物', value: 'all' }])
const searchResults = ref([])
const statsText = ref('等待选择帧...')
const searching = ref(false)
const targetPreviewVisible = ref(false)

let pendingCaptureNode = null

// Actions
const generateData = async () => {
  if (!transcriptionId.value) {
    MessageUtil.error("请输入 Transcription ID")
    return
  }
  loading.value = true
  try {
    const res = await generate_clothing_timeline(transcriptionId.value)
    MessageUtil.success(res?.message || "触发成功")
    setTimeout(() => {
      fetchData()
    }, 2000)
  } catch (e) {
    MessageUtil.error("生成失败: " + (e.msg || e.message || "未知错误"))
  } finally {
    loading.value = false
  }
}

const fetchData = async (item) => {
  const tid = transcriptionId.value.trim()
  if (!tid) {
    MessageUtil.error("请输入 Transcription ID")
    return
  }
  transcriptionId.value = tid
  loading.value = true
  highlights.value = {}
  searchResults.value = []
  try {
    const res = await get_clothing_timeline(tid)
    
    if (res && (res.status || res.transcription_id)) {
      timelineData.value = res
      currentVideoName.value = (item && (item.video_name || item.title)) || ''
      
      profileOptions.value = [{ title: '所有人物', value: 'all' }];
      const tracks = timelineData.value.tracks || {};
      let latest = 0;
      for (let pid in tracks) {
        profileOptions.value.push({ title: pid, value: pid });
        const history = tracks[pid] || [];
        if (history.length > 0) {
          const lastSec = history[history.length - 1].sec;
          if (lastSec > latest) latest = lastSec;
        }
      }

      // partial 结果可能只有音频或语速组件，时间轴长度不能只依赖服装轨迹。
      const speechTimeline = res.components?.speech_rate?.result?.timeline || [];
      const loudnessTimeline = res.components?.loudness?.result?.timeline || res.audio_data || [];
      for (const point of speechTimeline) {
        latest = Math.max(latest, Number(point.end_sec ?? point.start_sec ?? 0));
      }
      for (const point of loudnessTimeline) {
        latest = Math.max(latest, Number(point.sec ?? 0) + 1);
      }
      for (const segment of (res.speaker_timeline?.segments || [])) {
        latest = Math.max(latest, Number(segment.endSec ?? 0));
      }
      
      if (res.duration && res.duration > 0) {
        maxSeconds.value = res.duration;
      } else {
        maxSeconds.value = latest > 0 ? latest + 5 : 60;
      }

      // 如果有传入的 item 且带有 hit_time，自动跳转到对应位置
      if (item && item.hit_time && res.shooting_time) {
        const hitDate = new Date(item.hit_time)
        const startDate = new Date(res.shooting_time)
        const diffSecs = (hitDate.getTime() - startDate.getTime()) / 1000
        if (diffSecs >= 0) {
           setTimeout(() => {
              if (playerRef.value && playerRef.value.videoPlayer) {
                  playerRef.value.videoPlayer.currentTime = diffSecs
              }
           }, 500)
        }
      }
      
      if (timelineData.value.urls && timelineData.value.urls.length > 0) {
        videoUrl.value = timelineData.value.urls[0]
      } else if (timelineData.value.video_id) {
        videoUrl.value = `${BASE_URL}static/videos/${timelineData.value.video_id}.mp4`
      }
      
      if (timelineData.value.status === 'completed') {
        MessageUtil.success("数据加载成功")
      } else if (timelineData.value.status === 'processing') {
        MessageUtil.info("服饰分析仍在中后台处理... 可先进行其它剪辑操作")
      } else if (timelineData.value.status === 'none') {
        MessageUtil.info("该视频暂无换装轨，可正常浏览与商品剪切")
      }
    } else {
      MessageUtil.warning("未找到该 ID 的数据")
    }
  } catch (e) {
    MessageUtil.error("加载数据失败: " + (e.msg || e.message || "未知错误"))
  } finally {
    loading.value = false
  }
}

const onExport = () => {
  if (selectedSegments.value.length === 0) {
    MessageUtil.warning('请先在时间轴上（按住Shift拖动）选择要导出的区域')
    return
  }
  
  // 生成友好的默认备注名：视频名(截取前20字)_片段数x总时长
  const videoName = currentVideoName.value || ''
  const safeName = (videoName ? videoName.substring(0, 20) : transcriptionId.value.substring(0, 8))
    .replace(/[\\/:*?"<>|]/g, '_')
  const segCount = selectedSegments.value.length
  const totalSec = Math.round(totalDuration.value)
  const mm = Math.floor(totalSec / 60)
  const ss = (totalSec % 60).toString().padStart(2, '0')
  const timeStr = mm > 0 ? `${mm}m${ss}s` : `${ss}s`
  exportRemark.value = `${safeName}_${segCount}段${timeStr}`
  showExportDialog.value = true
}

const formatPoolSegmentTime = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0))
  const minutes = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const switchToClipPool = async () => {
  await store.dispatch('multiClipper/setActivePanel', 'pool')
  await router.push('/clipper/multi')
}

const clearSharedPool = () => {
  store.dispatch('multiClipper/clearAllGroups')
}

const reorderSharedGroup = ({ groupIndex, segments }) => {
  const group = store.state.multiClipper.groups[groupIndex]
  if (!group) return
  const segmentById = new Map((group.segments || []).map(segment => [segment.id, segment]))
  const reordered = segments.map(segment => segmentById.get(segment.id)).filter(Boolean)
  store.dispatch('multiClipper/updateGroupSegments', {
    groupIndex,
    segments: reordered,
    silent: true
  })
}

const confirmExport = async () => {
  loading.value = true
  try {
    const tid = normalizeId(transcriptionId.value || timelineData.value?.transcription_id)
    
    if (!tid) {
       MessageUtil.error("未找到有效的 Transcription ID")
       return
    }

    // 时间轴接口不一定返回 video_id；导出前按 transcription_id 解析真实素材 ID。
    // 这也会修复剪辑池中已经把 transcription_id 误存为 video_id 的旧片段。
    const exportSegments = await Promise.all(selectedSegments.value.map(async segment => {
      const segmentTid = normalizeId(segment.transcription_id || tid)
      const material = await getPoolMaterial(segmentTid)
      const storedVideoId = normalizeId(segment.video_id)
      const timelineVideoId = normalizeId(timelineData.value?.video_id)
      return {
        ...segment,
        video_id: normalizeId(material?.video_id)
          || (storedVideoId !== segmentTid ? storedVideoId : '')
          || (timelineVideoId !== segmentTid ? timelineVideoId : ''),
        transcription_id: segmentTid
      }
    }))

    const invalidSegment = exportSegments.find(segment => !segment.video_id)
    if (invalidSegment) {
      MessageUtil.error(`无法解析视频素材 ID: ${invalidSegment.transcription_id || '未知片段'}`)
      return
    }

    // Convert selection to Groups/Segments as required by MultiClipRequest
    let groups = []
    
    if (exportMerge.value) {
      // All segments into one group
      groups = [{
        name: exportRemark.value || `Merged_${tid}`,
        segments: exportSegments.map(s => ({
          video_id: s.video_id,
          transcription_id: s.transcription_id,
          start_time: Math.round(s.start * 1000),
          end_time: Math.round(s.end * 1000)
        }))
      }]
    } else {
      // Each segment is a separate group
      groups = exportSegments.map((s, idx) => ({
        name: `${exportRemark.value}_${idx + 1}`,
        segments: [{
          video_id: s.video_id,
          transcription_id: s.transcription_id,
          start_time: Math.round(s.start * 1000),
          end_time: Math.round(s.end * 1000)
        }]
      }))
    }

    const payload = {
      groups: groups,
      remark: exportRemark.value,
      merge_groups: exportMerge.value
    }
    
    // Call standard interface and unpack task IDs
    const res = await create_batch_slices(payload)
    
    if (res && res.task_ids && res.task_ids.length > 0) {
      exportTasks.value = res.task_ids.map((id, idx) => ({
        id: id,
        status: 'pending',
        message: '排队中...',
        file_url: null,
        name: groups[idx] ? groups[idx].name : `任务 ${idx + 1}`
      }))
      
      showProgressDialog.value = true
      startPolling()
      
      MessageUtil.success(`已提交 ${res.task_ids.length} 个导出任务，正在处理...`)
    } else {
      MessageUtil.success(`导出任务已提交`)
    }
    
    // We intentionally don't clear selectedSegments so users can re-export or tweak
    showExportDialog.value = false
  } catch (e) {
    console.error(e)
    MessageUtil.error('提交导出失败: ' + (e.msg || e.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

const startPolling = () => {
  if (pollingTimer) clearInterval(pollingTimer)
  pollingTimer = setInterval(checkAllTasks, 3000)
}

const checkAllTasks = async () => {
  let allDone = true
  for (let task of exportTasks.value) {
    if (task.status !== 'completed' && task.status !== 'failed') {
      allDone = false
      try {
        // According to old code, the endpoint does not require full baseUrl packaging if proxy configured,
        // but let's safely use axios directly or use an existing service function if it was exposed.
        const response = await axios.get(`${BASE_URL}slice_status/${task.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        
        const data = response.data?.data || response.data || response // Adjusting structure due to interceptor
        
        if (data && data.status) {
          task.status = data.status
          if (data.status === 'completed') {
            task.file_url = data.file_url
            task.message = '完成'
          } else if (data.status === 'failed') {
            task.status = 'failed'
            task.message = data.error_message || '失败'
          } else {
            task.message = data.status === 'processing' ? '处理中...' : '排队中...'
          }
        }
      } catch (e) {
        console.error(e)
      }
    }
  }
  
  if (allDone) {
    clearInterval(pollingTimer)
    pollingTimer = null
    MessageUtil.success('所有导出任务已完成')
  }
}

const clearFinishedTasks = () => {
  showProgressDialog.value = false
  exportTasks.value = []
}

const materialCache = new Map()

const normalizeId = value => value == null ? '' : String(value).trim()

const getPoolMaterial = async (tid) => {
  const normalizedTid = normalizeId(tid)
  if (!normalizedTid) return null
  if (!materialCache.has(normalizedTid)) {
    materialCache.set(normalizedTid, get_video_material_info(normalizedTid).catch(error => {
      materialCache.delete(normalizedTid)
      console.warn(`Failed to load transcript for shared pool segment ${normalizedTid}`, error)
      return null
    }))
  }
  return materialCache.get(normalizedTid)
}

const findPoolSegment = (segmentId) => {
  const groups = store.state.multiClipper.groups || []
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const segmentIndex = (groups[groupIndex].segments || []).findIndex(segment => segment.id === segmentId)
    if (segmentIndex !== -1) {
      return { groupIndex, segmentIndex, segment: groups[groupIndex].segments[segmentIndex] }
    }
  }
  return null
}

const enrichPoolSegment = async (segmentId, tid) => {
  const material = await getPoolMaterial(tid)
  const location = findPoolSegment(segmentId)
  if (!material || !location) return

  const startTime = Number(location.segment.start_time)
  const endTime = Number(location.segment.end_time)
  const allWords = material.word_df || material.wordDf || []
  const words = allWords
    .filter(word => Number(word.end_time) > startTime && Number(word.start_time) < endTime)
    .map(word => ({ ...word }))
  const text = words.map(word => word.word || word.text || '').join('')
  const urls = material.urls || location.segment.urls || []

  store.dispatch('multiClipper/updateSegment', {
    groupIndex: location.groupIndex,
    segmentIndex: location.segmentIndex,
    segment: {
      ...location.segment,
      video_id: normalizeId(material.video_id) || normalizeId(location.segment.video_id),
      transcription_id: normalizeId(location.segment.transcription_id || tid),
      video_name: material.video_name || material.title || location.segment.video_name,
      video_url: urls[0] || material.video_url || material.url || location.segment.video_url,
      urls,
      duration: material.duration || location.segment.duration,
      text: text || location.segment.text,
      words
    }
  })
}

const addSegment = async (seg) => {
  const tid = normalizeId(seg.transcription_id || transcriptionId.value || timelineData.value?.transcription_id)
  const material = await getPoolMaterial(tid)
  const startTime = Math.round(Number(seg.start) * 1000)
  const endTime = Math.round(Number(seg.end) * 1000)
  const segmentId = seg.id || `timeline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

  const groups = store.state.multiClipper.groups || []
  const currentGroupIndex = store.state.multiClipper.currentGroupIndex
  if (currentGroupIndex < 0 || !groups[currentGroupIndex]) {
    await store.dispatch('multiClipper/createGroup', `换装时间轴_${tid || '片段'}`)
  }

  await store.dispatch('multiClipper/addToGroup', {
    id: segmentId,
    video_id: normalizeId(material?.video_id) || normalizeId(seg.video_id) || normalizeId(timelineData.value?.video_id),
    transcription_id: tid,
    video_name: currentVideoName.value || `时间轴片段 ${formatPoolSegmentTime(seg.start)} - ${formatPoolSegmentTime(seg.end)}`,
    video_url: videoUrl.value,
    urls: videoUrl.value ? [videoUrl.value] : [],
    start_time: startTime,
    end_time: endTime,
    text: `${formatPoolSegmentTime(seg.start)} - ${formatPoolSegmentTime(seg.end)}`,
    words: []
  })

  enrichPoolSegment(segmentId, tid)
}

const removeSegment = (target) => {
  const locator = typeof target === 'number' ? selectedSegments.value[target] : target
  if (!locator) return
  const groupIndex = locator.groupIndex ?? locator._poolGroupIndex
  const group = store.state.multiClipper.groups[groupIndex]
  if (!group) return
  const segmentIndex = (group.segments || []).findIndex(segment => segment.id === (locator.segmentId || locator.id))
  if (segmentIndex !== -1) {
    store.dispatch('multiClipper/deleteSegment', { groupIndex, segmentIndex })
  }
}

const updateSegment = ({ idx, segment }) => {
  const locator = selectedSegments.value[idx]
  if (!locator) return
  const groupIndex = locator._poolGroupIndex
  const group = store.state.multiClipper.groups[groupIndex]
  const segmentIndex = (group?.segments || []).findIndex(item => item.id === locator.id)
  if (segmentIndex === -1) return

  const original = group.segments[segmentIndex]
  const startTime = Math.round(Number(segment.start) * 1000)
  const endTime = Math.round(Number(segment.end) * 1000)
  const words = (original.words || []).filter(word => Number(word.end_time) > startTime && Number(word.start_time) < endTime)
  store.dispatch('multiClipper/updateSegment', {
    groupIndex,
    segmentIndex,
    segment: {
      ...original,
      start_time: startTime,
      end_time: endTime,
      words
    }
  })
  enrichPoolSegment(original.id, original.transcription_id)
}

const onHistory = () => {
  showHistoryDialog.value = true
  fetchHistory()
}

const fetchHistory = async () => {
  historyLoading.value = true
  try {
    const response = await get_recent_user_slices()
    historySlices.value = response || []
  } catch (e) {
    MessageUtil.error("获取历史记录失败")
    historySlices.value = []
  } finally {
    historyLoading.value = false
  }
}

const getStatusColor = (item) => {
  return item.finish_ts ? 'success' : 'warning'
}

const getStatusIcon = (item) => {
  return item.finish_ts ? 'mdi-check-circle' : 'mdi-clock-outline'
}

const getStatusText = (item) => {
  return item.finish_ts ? '已完成' : '处理中'
}

const downloadFile = (fileUrl, filename) => {
  if (!fileUrl) {
    MessageUtil.error('文件不可用')
    return
  }
  
  try {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = filename || '下载文件'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('下载失败:', error)
    window.open(fileUrl, '_blank')
  }
}

const jumpToSec = async (val) => {
  let sec = 0
  let tid = null
  
  if (typeof val === 'number') {
    sec = val
  } else if (val && typeof val === 'object') {
    sec = val.sec !== undefined ? val.sec : (val.start !== undefined ? val.start : 0)
    tid = val.transcription_id
  }

  // Handle switching video if transcription ID is provided and different
  if (tid && tid !== transcriptionId.value) {
    console.log('Switching to transcription:', tid)
    transcriptionId.value = tid
    await fetchData()
    // Wait for video to be available after data fetch
    setTimeout(() => {
      if (playerRef.value?.videoPlayer) {
        playerRef.value.videoPlayer.currentTime = sec
      }
      playheadPos.value = sec
    }, 500)
    return
  }

  if (playerRef.value?.videoPlayer) {
    playerRef.value.videoPlayer.currentTime = sec
  }
  playheadPos.value = sec
  
  if (isLocked.value && selectedNode.value && Math.floor(sec) === selectedNode.value.sec) {
    nextTick(() => {
      captureVideoFrame(selectedNode.value);
    });
  }
}

const onDurationChange = (duration) => {
  if (!timelineData.value || !timelineData.value.duration) {
    maxSeconds.value = duration
  }
}

const onTimeUpdate = (val) => {
  // Handle both number (from our custom emit) and event (legacy)
  const sec = typeof val === 'number' ? val : val.target.currentTime;
  playheadPos.value = sec;
  
  if (isLocked.value && selectedNode.value && Math.floor(sec) === selectedNode.value.sec) {
    captureVideoFrame(selectedNode.value);
  }
}

const onVideoSeeked = () => {
  if (isLocked.value && selectedNode.value) {
    if (Math.floor(playerRef.value?.videoPlayer?.currentTime) === selectedNode.value.sec) {
      captureVideoFrame(selectedNode.value);
    }
  }
}

const onTimelineClickSec = (data) => {
  const { sec, profileId, node } = data
  
  if (!isLocked.value && node) {
    selectedNode.value = node;
    selectedProfile.value = profileId;
    selectedSec.value = node.sec;
    statsText.value = node.norm_pose ? "已选择姿态，点击搜索" : "该帧无姿态数据";
    searchResults.value = [];
    highlights.value = {};
    targetPreviewVisible.value = true;
    captureVideoFrame(node);
  }
  
  jumpToSec(sec);
}

const clearTarget = () => {
  selectedNode.value = null;
  selectedProfile.value = null;
  selectedSec.value = null;
  targetPreviewVisible.value = false;
  statsText.value = "等待选择帧...";
  searchResults.value = [];
  highlights.value = {};
  isLocked.value = false;
  
  const canvas = playerRef.value?.targetCanvas;
  if (canvas) {
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
}

const clearResults = () => {
  searchResults.value = [];
  highlights.value = {};
  isLocked.value = false;
}

const captureVideoFrame = (node) => {
  const v = playerRef.value?.hiddenVideo;
  if (!v || !node) return;
  pendingCaptureNode = node;
  v.currentTime = node.sec;
}

const onHiddenVideoSeeked = () => {
  const v = playerRef.value?.hiddenVideo;
  const cvs = playerRef.value?.targetCanvas;
  if (!v || !cvs || !pendingCaptureNode) return;
  
  const ctx = cvs.getContext('2d');
  const node = pendingCaptureNode;

  cvs.width = v.videoWidth;
  cvs.height = v.videoHeight;
  ctx.drawImage(v, 0, 0, cvs.width, cvs.height);
  
  if (node.norm_pose && node.pose_scale && node.pose_center) {
      const scale = node.pose_scale;
      const cx = node.pose_center[0] * cvs.width;
      const cy = node.pose_center[1] * cvs.height;
      
      ctx.strokeStyle = '#00ff00';
      ctx.fillStyle = '#00ff00';
      ctx.lineWidth = 4;
      
      const kpts = node.norm_pose.map(p => [
          p[0] * scale + cx,
          p[1] * scale + cy
      ]);
      
      kpts.forEach(pt => {
          if (pt[0] !== cx || pt[1] !== cy) {
              ctx.beginPath();
              ctx.arc(pt[0], pt[1], 5, 0, Math.PI * 2);
              ctx.fill();
          }
      });
      
      const connections = [
          [0,1], [0,2], [1,3], [2,4], [5,6], [5,7], [7,9], [6,8], [8,10],
          [11,12], [5,11], [6,12], [11,13], [13,15], [12,14], [14,16]
      ];
      
      ctx.beginPath();
      connections.forEach(([i, j]) => {
          const p1 = kpts[i], p2 = kpts[j];
          if (p1 && p2 && (p1[0] !== cx || p1[1] !== cy) && (p2[0] !== cx || p2[1] !== cy)) {
              ctx.moveTo(p1[0], p1[1]);
              ctx.lineTo(p2[0], p2[1]);
          }
      });
      ctx.stroke();
  }
  
  pendingCaptureNode = null;
}

const searchPoses = async () => {
  if (!selectedNode.value?.norm_pose) return;

  const tid = transcriptionId.value.trim();
  if (!tid) {
    MessageUtil.error("请输入 Transcription ID");
    return;
  }

  searching.value = true;
  statsText.value = "正在搜索...";
  highlights.value = {};
  isLocked.value = true;
  
  try {
    const response = await axios.post(`${BASE_URL}api/clothing-timeline/search-pose`, {
      transcription_id: tid,
      target_pose: selectedNode.value.norm_pose,
      target_scale: selectedNode.value.pose_scale,
      target_pos: selectedNode.value.rel_pos,
      target_upper_colors: selectedNode.value.upper_colors,
      target_lower_colors: selectedNode.value.lower_colors,
      filter_profile: filterProfile.value,
      scale_sensitive: scaleSensitive.value,
      pos_sensitive: posSensitive.value,
      filter_same_color: filterSameColor.value,
      color_threshold: colorThreshold.value
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
    
    // search-pose might return results directly or in .data
    const results = response.data?.data || response.data || response;

    searchResults.value = Array.isArray(results) ? results.map(r => ({
      ...r,
      score: r.similarity // Sub-component expects 'score'
    })) : [];
    
    statsText.value = `找到 ${searchResults.value.length} 个相似动作`;
    
    const newHighlights = {};
    searchResults.value.forEach(res => {
      if (!newHighlights[res.profileId]) {
        newHighlights[res.profileId] = [];
      }
      newHighlights[res.profileId].push({ sec: res.sec });
    });
    highlights.value = newHighlights;
    
  } catch(e) {
    MessageUtil.error("搜索失败: " + (e.response?.data?.message || e.message));
    statsText.value = "搜索失败";
  } finally {
    searching.value = false;
  }
}

const onDownloadImage = async () => {
  const tid = transcriptionId.value.trim() || timelineData.value?.transcription_id
  if (!tid) {
    MessageUtil.error("未找到有效的 Transcription ID")
    return
  }
  
  // 始终下载预览区（当前播放头）所处位置的画面
  const sec = playheadPos.value
  
  try {
    MessageUtil.info('正在请求当前播放帧图片...')
    await download_frame_image(tid, sec)
    MessageUtil.success('预览帧下载成功')
  } catch (e) {
    MessageUtil.error('下载单帧失败: ' + (e.response?.data?.detail || e.message))
  }
}

const addClipToPool = (duration) => {
  const centerSec = playheadPos.value
  const half = duration / 2
  const start = Math.max(0, centerSec - half)
  const end = Math.min(maxSeconds.value, centerSec + half)
  
  addSegment({ start, end })
  MessageUtil.success(`已添加 ${duration}秒 片段到剪辑池`)
}

const togglePlay = () => {
  const v = playerRef.value?.videoPlayer;
  if (!v) return;
  
  if (v.paused) {
    v.play().catch(e => console.error("Play failed:", e));
  } else {
    v.pause();
  }
}

const handleKeyDown = (e) => {
  if (e.code === 'Space') {
    const activeElement = document.activeElement;
    const isInput = activeElement.tagName === 'INPUT' || 
                    activeElement.tagName === 'TEXTAREA' || 
                    activeElement.isContentEditable ||
                    activeElement.tagName === 'SELECT';
    
    if (!isInput) {
      e.preventDefault();
      togglePlay();
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
})
</script>

<style scoped>
.clothing-timeline-refactor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #121212;
  overflow: hidden;
}

.main-layout-content {
  flex: 1;
  overflow: hidden;
}

:deep(.splitpanes--vertical > .splitpanes__splitter) {
  width: 4px;
  background-color: #333;
}

:deep(.splitpanes--horizontal > .splitpanes__splitter) {
  height: 4px;
  background-color: #333;
}

:deep(.splitpanes__pane) {
  background-color: #1e1e1e;
}

.timeline-toolbar {
  height: 40px;
  background-color: #252525;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  flex-shrink: 0;
}

.toolbar-title {
  color: #aaa;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.track-layer-filter-btn {
  height: 26px !important;
  border: 1px solid #444;
  color: #bbb;
  text-transform: none;
}

.track-layer-filter-menu {
  min-width: 220px;
  padding-bottom: 6px;
}

.track-layer-filter-header {
  height: 38px;
  padding: 0 8px 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ddd;
  font-size: 12px;
}

.track-layer-filter-option {
  height: 36px;
  padding: 0 12px 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ccc;
  font-size: 12px;
  cursor: pointer;
}

.track-layer-filter-option:hover {
  background-color: #303030;
}

.toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.toolbar-jump-box {
  display: flex;
  align-items: center;
  background-color: #181818;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0 4px 0 8px;
  gap: 8px;
  height: 28px;
}

.jump-label {
  font-size: 11px;
  color: #777;
}

.time-display-clickable {
  font-family: monospace;
  font-size: 13px;
  color: #4db6ac;
  cursor: pointer;
  padding: 0 4px;
  border-radius: 2px;
  min-width: 150px;
  text-align: center;
}

.time-display-clickable:hover {
  background-color: #333;
}

.time-jump-input {
  font-family: monospace;
  font-size: 13px;
  color: #fff;
  background: transparent;
  border: none;
  outline: none;
  width: 150px;
  text-align: center;
  border-bottom: 1px solid #4db6ac;
}

.nav-segment-controls {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.nav-segment-btn {
  background-color: #181818 !important;
  border: 1px solid #444 !important;
  color: #aaa !important;
  height: 28px !important;
  font-size: 11px !important;
  min-width: unset !important;
  padding: 0 8px !important;
  text-transform: none !important;
  border-radius: 4px !important;
}

.nav-segment-btn:hover {
  background-color: #252525 !important;
  color: #4db6ac !important;
  border-color: #4db6ac !important;
}

.locate-btn {
  color: #777 !important;
}

.locate-btn:hover {
  color: #4db6ac !important;
}
</style>
